import { Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { normalizeRating, formatRatingValue } from '@/lib/productReview'

function starFillFraction(starIndex: number, rating: number): number {
  return Math.max(0, Math.min(1, rating - (starIndex - 1)))
}

function StarGlyph({
  fill,
  className,
}: {
  fill: number
  className?: string
}) {
  const amount = Math.max(0, Math.min(1, fill))

  if (amount >= 1) {
    return (
      <Star
        className={cn(className, 'fill-gold-400 text-gold-400')}
        strokeWidth={1.5}
      />
    )
  }

  if (amount <= 0) {
    return <Star className={cn(className, 'text-zinc-600')} strokeWidth={1.5} />
  }

  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      <Star className="text-zinc-600" strokeWidth={1.5} />
      <span
        className="absolute inset-y-0 left-0 overflow-hidden"
        style={{ width: `${amount * 100}%` }}
        aria-hidden
      >
        <span className={cn('inline-flex', className)}>
          <Star className="fill-gold-400 text-gold-400" strokeWidth={1.5} />
        </span>
      </span>
    </span>
  )
}

interface StarRatingInputProps {
  value: number
  onChange: (rating: number) => void
  disabled?: boolean
  size?: 'sm' | 'md'
}

export function StarRatingInput({
  value,
  onChange,
  disabled = false,
  size = 'md',
}: StarRatingInputProps) {
  const starSize = size === 'sm' ? 'h-5 w-5' : 'h-8 w-8'
  const displayValue = value > 0 ? normalizeRating(value) : 0

  return (
    <div className="space-y-2">
      <div
        className="flex items-center gap-1"
        role="slider"
        aria-label="Product rating"
        aria-valuemin={1}
        aria-valuemax={5}
        aria-valuenow={displayValue || undefined}
        aria-valuetext={
          displayValue > 0 ? `${displayValue} out of 5 stars` : 'No rating selected'
        }
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={cn(
              'relative inline-flex',
              disabled && 'opacity-50'
            )}
          >
            <StarGlyph
              fill={starFillFraction(star, displayValue)}
              className={starSize}
            />
            {!disabled && (
              <>
                <button
                  type="button"
                  className="absolute inset-y-0 left-0 w-1/2 cursor-pointer rounded-l-md"
                  aria-label={`${star - 0.5} stars`}
                  onClick={() => onChange(normalizeRating(star - 0.5))}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 w-1/2 cursor-pointer rounded-r-md"
                  aria-label={`${star} stars`}
                  onClick={() => onChange(normalizeRating(star))}
                />
              </>
            )}
          </span>
        ))}
      </div>
      <p className="text-xs text-zinc-500">
        {displayValue > 0 ? (
          <>
            <span className="font-medium text-gold-300">{formatRatingValue(displayValue)}</span>
            {' / 5 — tap left or right of a star for half ratings'}
          </>
        ) : (
          'Tap a star for whole or half ratings (e.g. 4.5)'
        )}
      </p>
    </div>
  )
}

interface StarRatingDisplayProps {
  rating: number
  size?: 'sm' | 'md' | 'lg'
  className?: string
  showValue?: boolean
}

export function StarRatingDisplay({
  rating,
  size = 'sm',
  className,
  showValue = false,
}: StarRatingDisplayProps) {
  const starSize =
    size === 'lg' ? 'h-5 w-5' : size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5'
  const valueClass =
    size === 'lg'
      ? 'text-sm font-semibold'
      : size === 'md'
        ? 'text-xs font-medium'
        : 'text-xs font-medium'
  const safeRating = Math.max(0, Math.min(5, rating))

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <span className="inline-flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarGlyph
            key={star}
            fill={starFillFraction(star, safeRating)}
            className={starSize}
          />
        ))}
      </span>
      {showValue && safeRating > 0 && (
        <span className={cn('text-gold-300', valueClass)}>
          {formatRatingValue(safeRating)}
        </span>
      )}
    </span>
  )
}
